import { useCallback, useMemo, useRef } from "react"
import { Line } from "react-chartjs-2"
import { Chart, registerables } from "chart.js"
import styled from "styled-components"

Chart.register(...registerables)

const ChartContainer = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
`

const TooltipEl = styled.div`
  position: absolute;
  background: rgba(15, 23, 42, 0.9);
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 8px;
  pointer-events: none;
  z-index: 10;
  transition: opacity 0.15s;
  white-space: nowrap;
`

const TooltipTitle = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: #ffffff;
  margin-bottom: 2px;
`

const TooltipBody = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: #ffffff;
`

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) return { r: 34, g: 197, b: 94 }
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  }
}

interface Props {
  data: number[]
  labels?: string[]
  color?: string
}

export const SimpleLineChart = ({ data, labels, color = "#22C55E" }: Props) => {
  const { r, g, b } = useMemo(() => hexToRgb(color), [color])
  const tooltipRef = useRef<HTMLDivElement>(null)

  const chartLabels = useMemo(() => labels ?? data.map((_, i) => (i + 1).toString()), [data.length, labels])

  const externalTooltipHandler = useCallback(
    (context: {
      chart: Chart
      tooltip: { opacity: number; caretX: number; caretY: number; title: string[]; body: Array<{ lines: string[] }> }
    }) => {
      const { tooltip } = context
      const el = tooltipRef.current
      if (!el) return

      if (tooltip.opacity === 0) {
        el.style.opacity = "0"
        return
      }

      const title = tooltip.title?.[0] ?? ""
      const bodyLine = tooltip.body?.[0]?.lines?.[0] ?? ""

      const titleEl = el.querySelector("[data-tooltip-title]")
      const bodyEl = el.querySelector("[data-tooltip-body]")
      if (titleEl) titleEl.textContent = title
      if (bodyEl) bodyEl.textContent = bodyLine

      el.style.opacity = "1"
      el.style.left = tooltip.caretX + "px"
      el.style.top = tooltip.caretY + "px"
      el.style.transform = "translate(-50%, -120%)"
    },
    [],
  )

  const chartData = useMemo(
    () => ({
      labels: chartLabels,
      datasets: [
        {
          label: "",
          data,
          borderColor: (context: { chart: Chart }) => {
            const ctx = context.chart.ctx
            const chart = context.chart
            const gradient = ctx.createLinearGradient(0, 0, chart.width, 0)
            gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0)`)
            gradient.addColorStop(0.4, `rgba(${r}, ${g}, ${b}, 1)`)
            return gradient
          },
          backgroundColor: (context: { chart: Chart }) => {
            const ctx = context.chart.ctx
            const chart = context.chart
            const gradient = ctx.createLinearGradient(0, 0, 0, chart.height)
            gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.3)`)
            gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`)
            return gradient
          },
          borderWidth: 2,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointHoverBorderWidth: 2,
          pointHoverBackgroundColor: "#ffffff",
          fill: true,
        },
      ],
    }),
    [chartLabels, data, r, g, b],
  )

  const options = useMemo(
    () => ({
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: false,
          mode: "index" as const,
          intersect: false,
          external: externalTooltipHandler,
        },
      },
      scales: {
        x: { display: false, grid: { display: false }, border: { display: false } },
        y: { display: false, grid: { display: false }, border: { display: false } },
      },
    }),
    [externalTooltipHandler],
  )

  return (
    <ChartContainer>
      <Line data={chartData} options={options} />
      <TooltipEl ref={tooltipRef} style={{ opacity: 0 }}>
        <TooltipTitle data-tooltip-title />
        <TooltipBody data-tooltip-body />
      </TooltipEl>
    </ChartContainer>
  )
}
